import { useRef, useState } from "react";
import { CheckCircle2, Loader2, Paperclip, RotateCcw, X } from "lucide-react";
import api, { apiError } from "@/lib/api";

const MAX_BYTES = 25 * 1024 * 1024;

const ACCEPT = {
  screenshot: { attr: "image/jpeg,image/png", exts: ["jpg", "jpeg", "png"], label: "JPG or PNG" },
  invoice: {
    attr: "application/pdf",
    exts: ["pdf"],
    label: "PDF only",
  },
  content: {
    attr: "image/jpeg,image/png,video/mp4,video/quicktime",
    exts: ["jpg", "jpeg", "png", "mp4", "mov"],
    label: "JPG, PNG, MP4 or MOV",
  },
};

/**
 * Uploads files to private storage and reports back the stored file ids.
 * Nothing is submitted to the parent form until every upload has succeeded.
 */
export function Uploader({ kind, multiple = false, files, onChange, testId }) {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]); // {key, file, progress, status, id, error}
  const accept = ACCEPT[kind] || ACCEPT.screenshot;

  const sync = (next) => {
    setItems(next);
    onChange(next.filter((i) => i.status === "done").map((i) => ({ id: i.id, name: i.file.name })));
  };

  const upload = async (entry, list) => {
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", entry.file);
    const update = (patch) => {
      list = list.map((i) => (i.key === entry.key ? { ...i, ...patch } : i));
      sync(list);
    };
    update({ status: "uploading", progress: 0, error: null });
    try {
      const { data } = await api.post("/files", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (event.total)
            update({ progress: Math.round((event.loaded / event.total) * 100), status: "uploading" });
        },
      });
      update({ status: "done", progress: 100, id: data.id });
    } catch (e) {
      update({ status: "error", error: apiError(e, "Upload failed. Please retry.") });
    }
  };

  const handleFiles = (selected) => {
    const incoming = Array.from(selected);
    let list = multiple ? [...items] : [];
    const queued = [];
    incoming.forEach((file) => {
      const ext = file.name.split(".").pop()?.toLowerCase();
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`;
      if (!accept.exts.includes(ext)) {
        list.push({ key, file, status: "error", progress: 0, error: `Only ${accept.label} files are allowed.` });
        return;
      }
      if (file.size > MAX_BYTES) {
        list.push({
          key,
          file,
          status: "error",
          progress: 0,
          error: `${(file.size / 1024 / 1024).toFixed(1)} MB exceeds the 25 MB limit.`,
        });
        return;
      }
      const entry = { key, file, status: "queued", progress: 0 };
      list.push(entry);
      queued.push(entry);
    });
    sync(list);
    queued.forEach((entry) => upload(entry, list));
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (key) => sync(items.filter((i) => i.key !== key));
  const retry = (key) => {
    const entry = items.find((i) => i.key === key);
    if (entry) upload(entry, items);
  };

  const busy = items.some((i) => i.status === "uploading");

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept={accept.attr}
        multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={`${testId}-button`}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#D9D2C6] bg-[#FBFAF7] px-4 py-5 text-sm font-semibold text-[#111111] transition-colors hover:border-[#F2811D]"
      >
        <Paperclip className="h-4 w-4" />
        {multiple ? (kind === "screenshot" ? "Choose screenshots" : "Choose files") : "Choose file"}
      </button>
      <p className="mt-2 text-xs body-text">
        {accept.label} · up to 25 MB per file{multiple ? " · you can add more than one" : ""}
      </p>

      {items.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`${testId}-list`}>
          {items.map((item) => (
            <li
              key={item.key}
              className="rounded-xl border border-[#E6E1D8] bg-white px-3.5 py-3"
              data-testid={`${testId}-item`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.file.name}</span>
                <span className="flex items-center gap-2">
                  {item.status === "uploading" && (
                    <span className="flex items-center gap-1.5 text-xs body-text">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {item.progress}%
                    </span>
                  )}
                  {item.status === "done" && (
                    <CheckCircle2 className="h-4 w-4 text-[#1B6B3A]" data-testid={`${testId}-done`} />
                  )}
                  {item.status === "error" && (
                    <button
                      type="button"
                      onClick={() => retry(item.key)}
                      data-testid={`${testId}-retry`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#F2811D]"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Retry
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(item.key)}
                    aria-label="Remove file"
                    className="rounded-full p-1 text-[#8a8378] hover:bg-[#F7F4EF]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
              {item.status === "uploading" && (
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#F0ECE4]">
                  <div
                    className="h-full rounded-full bg-[#F2811D] transition-[width]"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
              )}
              {item.error && (
                <p className="mt-2 text-xs font-medium text-[#B3261E]" data-testid={`${testId}-error`}>
                  {item.error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {busy && (
        <p className="mt-2 text-xs font-semibold text-[#8A5A00]">
          Wait for uploads to finish before submitting.
        </p>
      )}
    </div>
  );
}

export function uploaderBusy(files) {
  return !files || files.length === 0;
}
